import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Logo } from '@/components/Logo';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { z } from 'zod';

const setPasswordSchema = z
  .object({
    password: z.string().min(8, 'Password must be at least 8 characters'),
    confirmPassword: z.string(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
  });

export default function SetPassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const { updatePassword, session, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!authLoading && !session) {
      navigate('/login', { replace: true });
    }
  }, [session, authLoading, navigate]);

  if (authLoading || !session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
        <p className="font-sans text-wt-text-secondary">Loading…</p>
      </div>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    const validation = setPasswordSchema.safeParse({ password, confirmPassword });
    if (!validation.success) {
      setError(validation.error.errors[0].message);
      setLoading(false);
      return;
    }

    const { error: updateError } = await updatePassword(password);
    if (updateError) {
      setError(updateError.message);
      setLoading(false);
      return;
    }
    navigate('/app', { replace: true });
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-wt-bg-primary text-wt-text-primary p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center mb-4">
            <Logo variant="fullTransparent" size="h-16" />
          </div>
          <h1 className="font-sans text-2xl font-bold text-wt-text-primary">Set new password</h1>
          <p className="font-sans text-wt-text-secondary mt-1 text-sm">
            Choose a strong password for your Watt Tower account.
          </p>
        </div>

        <div className="rounded-lg border border-wt-border-default bg-wt-bg-secondary p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password" className="font-sans text-wt-text-secondary">
                New password
              </Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                autoComplete="new-password"
                className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="font-sans text-wt-text-secondary">
                Confirm password
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                autoComplete="new-password"
                className="bg-wt-bg-tertiary border-wt-border-default text-wt-text-primary placeholder:text-wt-text-muted focus:border-wt-slate-blue"
              />
            </div>

            {error && (
              <div className="p-3 rounded-md bg-destructive/10 border border-destructive/20 text-destructive text-sm font-sans">
                {error}
              </div>
            )}

            <Button
              type="submit"
              className="w-full h-11 bg-primary font-sans font-semibold text-wt-bg-primary hover:bg-wt-green-muted"
              disabled={loading}
            >
              {loading ? 'Updating…' : 'Update password'}
            </Button>
          </form>
        </div>

        <p className="text-center font-sans text-xs text-wt-text-muted mt-6">
          <a href="/login" className="text-wt-text-secondary hover:text-wt-text-primary">
            Back to sign in
          </a>
        </p>
      </div>
    </div>
  );
}
